import type { SuperHubStore } from '..';
import type useReadmeApi from '@core/hooks/useReadmeApi';
import type { HTTPError } from '@core/utils/types/errors';
import type { CreateChangelogType, ReadChangelogType } from '@readme/api-contracts/schemas/changelog';
import type {
  ReadCustomBlockGitCollectionType,
  ReadCustomBlockGitType,
} from '@readme/api-contracts/schemas/customblock';
import type { CreateCustomPageType, CustomPageReadType } from '@readme/api-contracts/schemas/custompage';
import type { ReadDiscussType } from '@readme/api-contracts/schemas/discuss';
import type { ErrorType } from '@readme/api-contracts/schemas/error';
import type { RecipeReadType } from '@readme/api-contracts/schemas/recipe';
import type { CreateGuideType, ReadGuideType } from '@readme/api/mappings/page/guide/types';
import type { CreateReferenceType, ReadReferenceType } from '@readme/api/mappings/page/reference/types';
import type { WritableDeep } from 'type-fest';
import type { StateCreator } from 'zustand';

import { fetcher } from '@core/hooks/useReadmeApi';
import { projectStore } from '@core/store';
import { actionLog, isClient } from '@core/store/util';
import { mutate } from '@Hub/swr';

import {
  hasRemoteDocumentEditableConflict,
  isChangelog,
  isCustomPage,
  isDiscussion,
  isGuidesPage,
  isRecipe,
  isReferencePage,
} from './util';

export type SuperHubDocumentData =
  | CustomPageReadType['data']
  | ReadChangelogType['data']
  | ReadGuideType['data']
  | ReadReferenceType['data'];
export type SuperHubGuideReferencePage = ReadGuideType['data'] | ReadReferenceType['data'];

type UpdateSource = 'ai-agent' | 'user';
/**
 * Request payload type for "create" endpoints.
 */
type SuperHubCreateDocumentData = CreateChangelogType | CreateCustomPageType | CreateGuideType | CreateReferenceType;

/**
 * Response document type from "create" API endpoints.
 */
type CreateDocumentResponse<CreateDocument> = CreateDocument extends CreateChangelogType
  ? ReadChangelogType
  : CreateDocument extends CreateCustomPageType
    ? CustomPageReadType
    : CreateDocument extends CreateGuideType
      ? ReadGuideType
      : ReadReferenceType;

interface SuperHubDocumentSliceState {
  /**
   * Indicates whether the current API endpoint page has limited functionality
   */
  apiDesignerLimited: {
    /**
     * List of unsupported feature names from `api.stats` that are blocking the API Designer.
     */
    blockingFeatures: string[];

    /**
     * Indicates whether API Designer is limited based on `api.stats` features.
     */
    byFeature: boolean;

    /**
     * Indicates whether API Designer is limited based on project-level settings.
     */
    byProjectLevel: boolean;
  } | null;

  /**
   * Collection of custom blocks that are used in the current document. This
   * data is used to hydrate the MarkdownEditor with a document's initial custom
   * block data as well as to provide RDMD with the necessary custom block values
   * when parsing the document.
   */
  customBlocks: ReadCustomBlockGitCollectionType['data'];

  /**
   * Primary data source for the current page.
   */
  data: SuperHubDocumentData | null;

  /**
   * Store the current document's ETag so we can send it with If-Match on
   * update requests for optimistic concurrency control.
   */
  etag: string | null;

  /**
   * Whether document data is currently being fetched via API.
   */
  isLoading: boolean;

  /**
   * Indicates this slice has been initialized on both server and client. We use
   * this to differentiate how the state should update during SSR vs CSR. On the
   * server, the state should never prevent updates or retain previously
   * existing data. This is only safe to do once this flag is flipped to `true`.
   * @see initialize
   */
  isReady: boolean;

  /**
   * Indicates whether a document save request is currently pending.
   */
  isSaving: boolean;

  /**
   * Error message that is set when an MDX validation error occurs.
   */
  mdxValidationError: string | null;

  /**
   * The MDX content that was attempted to be saved when an MDX validation error occurred.
   * This allows the AI agent to see the invalid content and fix it.
   */
  mdxValidationErrorPageContent: string | null;

  /**
   * Whether the latest prefetch attempt failed for the current slug.
   */
  prefetchFailed: boolean;

  /**
   * Whether the remote document changed while the editor is open.
   */
  remoteDocumentChanged: boolean;

  /**
   * Error object that is set when a document save request fails.
   */
  saveError: ErrorType | null;

  /**
   * Holds reference to the SWR request key that was used to fetch API definition
   * data from the API. This key can then be used by SWR's `mutate()` function
   * to update cached data to optimistically reflect a changed document while
   * a mutation request is in flight.
   */
  swrKeyApiDefinition: ReturnType<typeof useReadmeApi>['swrKey'];

  /**
   * SWR key used to fetch the current `customBlocks` collection from the API.
   * This key can then be used by SWR's `mutate()` function update cached data to
   * optimistically reflect a changed document while a mutation request is in flight.
   */
  swrKeyCustomBlocks: ReturnType<typeof useReadmeApi>['swrKey'];

  /**
   * Holds reference to the SWR request key that was used to fetch document
   * data from the API. This key can then be used by SWR's `mutate()` function
   * to update cached data to optimistically reflect a changed document while
   * a mutation request is in flight.
   * @see https://swr.vercel.app/docs/mutation
   */
  swrKeyDocument: ReturnType<typeof useReadmeApi>['swrKey'];

  /**
   * Source of document updates (e.g., 'user', 'external', 'ai').
   * This helps distinguish between different types of updates.
   */
  updateSource: UpdateSource | null;
}

interface SuperHubDocumentSliceAction {
  /**
   * Creates a new page document inside the provided category.
   * @todo the typing of `newDocument` should be updated to a union of CreateGuideType and
   * CreateReferencePageType once we have the APIV2 typings for the reference section.
   */
  createDocument<CreateDocument extends SuperHubCreateDocumentData>(
    newDocument: CreateDocument,
    branch?: string,
  ): Promise<CreateDocumentResponse<CreateDocument>['data']>;

  /**
   * Generates the URL path to reach the API endpoint for the current section
   * and document. Used by actions to create, edit or view documents. By default
   * this will use the current branch for versioned resource, but that
   * endpoint segment can be overriden with the `branch` argument.
   *
   * @example
   * ```ts
   * getApiUrl()
   *   => '/subdomain/api-next/v2/branches/1.0/guides'
   * getApiUrl({ slug: 'page' })
   *   => '/subdomain/api-next/v2/branches/1.0/guides/page'
   * getApiUrl({ branch: '1.0_froyo': slug: 'page' })
   *   => '/subdomain/api-next/v2/branches/1.0_froyo/guides/page'
   * getApiUrl({ branch: '1.0_froyo': slug: 'page', querystring: new URLSearchParams({ cleanup_definition: true }) })
   *   => '/subdomain/api-next/v2/branches/1.0_froyo/reference/page?cleanup_definition=true'
   * ```
   */
  getApiUrl: (args?: { slug?: string; branch?: string; querystring?: URLSearchParams }) => string | null;

  /**
   * Returns a changelog document if one exists and is loaded.
   */
  getChangelogData: () => ReadChangelogType['data'] | null;

  /**
   * Returns a custom page document if one exists and is loaded.
   */
  getCustomPageData: () => CustomPageReadType['data'] | null;

  /**
   * Returns a discussion document if one exists and is loaded.
   */
  getDiscussionData: () => ReadDiscussType['data'] | null;

  /**
   * Returns a guides page document if one exists and is loaded.
   */
  getGuidesPageData: () => ReadGuideType['data'] | null;

  /**
   * Returns a recipe document if one exists and is loaded.
   */
  getRecipeData: () => RecipeReadType['data'] | null;

  /**
   * Returns a reference page document if one exists and is loaded.
   */
  getReferencePageData: () => ReadReferenceType['data'] | null;

  /**
   * Updates the state with data and loading states. Typically called when
   * initializing this slice with SSR data or when hydrating it from an API.
   */
  initialize: (
    payload: Pick<SuperHubDocumentSliceState, 'data' | 'isLoading'> & {
      customBlocks?: SuperHubDocumentSliceState['customBlocks'];
      etag?: SuperHubDocumentSliceState['etag'];
      swrKeyCustomBlocks?: SuperHubDocumentSliceState['swrKeyCustomBlocks'];
      swrKeyDocument?: SuperHubDocumentSliceState['swrKeyDocument'];
      swrKeyApiDefinition?: SuperHubDocumentSliceState['swrKeyApiDefinition'];
    },
  ) => void;

  /**
   * Revalidates the document and pulls down the latest data from the API.
   */
  revalidate: () => Promise<SuperHubDocumentData[] | undefined> | undefined;

  /**
   * Stores a client-side MDX validation error using the same UI/state surface
   * as server-side validation failures.
   */
  setClientMdxValidationError: (message: string, content?: string | null) => void;

  /**
   * Updates the prefetch failure flag for the current document.
   */
  setPrefetchFailed: (prefetchFailed: boolean) => void;

  /**
   * Tracks whether the remote document changed while the editor is open.
   */
  setRemoteDocumentChanged: (remoteDocumentChanged: boolean) => void;

  /**
   * Set the update source to indicate where updates are coming from.
   */
  setUpdateSource: (updateSource: UpdateSource | null) => void;

  /**
   * Create or update a `customBlocks` item in the current state.
   */
  updateCustomBlock: (block: ReadCustomBlockGitType['data']) => void;

  /**
   * Persist changes of the current document to the API.
   */
  updateDocument: (
    nextDocument: Partial<SuperHubDocumentData>,
    options?: {
      branch?: string;
      skipEtag?: boolean;
      slug?: string;
      source?: UpdateSource;
    },
  ) => Promise<SuperHubDocumentData | null>;
}

export interface SuperHubDocumentSlice {
  /**
   * State slice containing fields and actions that are relevant when viewing
   * and editing page documents in SuperHub.
   */
  document: SuperHubDocumentSliceAction & SuperHubDocumentSliceState;
}

const initialState: SuperHubDocumentSliceState = {
  customBlocks: [],
  data: null,
  etag: null,
  apiDesignerLimited: null,
  isLoading: false,
  prefetchFailed: false,
  remoteDocumentChanged: false,
  isReady: false,
  isSaving: false,
  mdxValidationError: null,
  mdxValidationErrorPageContent: null,
  saveError: null,
  updateSource: null,
  swrKeyCustomBlocks: null,
  swrKeyDocument: null,
  swrKeyApiDefinition: null,
};

/**
 * Creates a state slice containing all fields related to our page document.
 */
export const createSuperHubDocumentSlice: StateCreator<
  SuperHubDocumentSlice & SuperHubStore,
  [['zustand/devtools', never], ['zustand/immer', never]],
  [],
  SuperHubDocumentSlice
> = (set, get) => ({
  document: {
    ...initialState,

    setClientMdxValidationError: (message, content = null) => {
      set(
        state => {
          state.document.mdxValidationError = message;
          state.document.mdxValidationErrorPageContent = content;
          state.editor.isSavingDisabled = true;
        },
        false,
        actionLog('document.setClientMdxValidationError', { content, message }),
      );
    },

    createDocument: async (newDocument, branch) => {
      const requestUrl = get().document.getApiUrl({ branch });
      if (!requestUrl) {
        throw new Error('Missing required request URL');
      }

      set(
        state => {
          state.document.isSaving = true;
        },
        false,
        actionLog('document.createDocument.pending', newDocument),
      );

      try {
        const { data } = await fetcher<CreateDocumentResponse<typeof newDocument>>(requestUrl, {
          body: JSON.stringify(newDocument),
          method: 'POST',
        });

        set(
          state => {
            state.document.data = data;
            state.document.isSaving = false;
            state.document.mdxValidationError = null;
            state.document.mdxValidationErrorPageContent = null;
          },
          false,
          actionLog('document.createDocument.fulfilled', newDocument),
        );

        // Revalidate the sidebar to include this newly created page.
        get().sidebar.revalidate();
        return data;
      } catch (error) {
        const { info } = error as HTTPError;
        // @ts-expect-error: @TODO
        const mdxValidationError = info.title?.match(/MDX validation/) ? info.detail : null;

        set(
          state => {
            state.document.isSaving = false;
            state.document.saveError = info as ErrorType;
            if (mdxValidationError) {
              state.document.mdxValidationError = mdxValidationError;
              state.document.mdxValidationErrorPageContent = newDocument.content?.body || null;
              state.editor.isSavingDisabled = true;
            }
          },
          false,
          actionLog('document.createDocument.rejected', newDocument),
        );
        throw error;
      }
    },

    getApiUrl: ({ slug, branch, querystring } = {}) => {
      const { getApiBaseUrl, routeSection } = get();
      if (!routeSection) return null;

      const apiType = {
        changelog: 'changelogs',
        discuss: 'discuss',
        docs: 'guides',
        reference: 'reference',
        page: 'custom_pages',
        recipes: 'recipes',
      }[routeSection];

      // Some sections are not versioned. So we strip away that segment
      // from the API url for those cases.
      const nonVersionedTypes = ['changelogs', 'discuss'];
      const baseUrl = nonVersionedTypes.includes(apiType) ? getApiBaseUrl({ branch: null }) : getApiBaseUrl({ branch });

      let url = [baseUrl, apiType, slug].filter(Boolean).join('/');
      if (querystring) {
        url += `?${querystring.toString()}`;
      }

      return url;
    },

    getChangelogData: () => {
      const data = get()?.document.data;
      return isChangelog(data) ? data : null;
    },

    getDiscussionData: () => {
      const data = get()?.document.data as ReadDiscussType['data'] | SuperHubDocumentData | null;
      return isDiscussion(data) ? data : null;
    },

    getCustomPageData: () => {
      const data = get()?.document.data;
      return isCustomPage(data) ? data : null;
    },

    getGuidesPageData: () => {
      const data = get().document.data;
      return isGuidesPage(data) ? data : null;
    },

    getRecipeData: () => {
      const data = get()?.document.data as RecipeReadType['data'] | SuperHubDocumentData | null;
      return isRecipe(data) ? data : null;
    },

    getReferencePageData: () => {
      const data = get()?.document.data;
      return isReferencePage(data) ? data : null;
    },

    initialize: payload => {
      const { data, etag, isLoading, swrKeyDocument, customBlocks, swrKeyCustomBlocks, swrKeyApiDefinition } = payload;
      const currentDocument = get().document.data;
      const remoteDocumentChanged =
        get().isEditing &&
        get().document.isReady &&
        !isLoading &&
        !!currentDocument &&
        !!data &&
        currentDocument.slug === data.slug &&
        hasRemoteDocumentEditableConflict(currentDocument, data);

      if (remoteDocumentChanged) {
        get().document.setRemoteDocumentChanged(true);
        return;
      }

      const nextData = {
        data: data ?? get().document.data,
        customBlocks: isLoading ? get().document.customBlocks : (customBlocks ?? get().document.customBlocks),
        etag: etag !== undefined ? etag : get().document.etag,
        isLoading,
      };

      // Only continue with a state update if there are changed values. This
      // quiets down the redux devtools action logs to only contain actions
      // that contain differences.
      const hasChanges = Object.entries(nextData).some(([key, value]) => {
        return JSON.stringify(value) !== JSON.stringify(get().document[key]);
      });

      if (!hasChanges) return;

      set(
        state => {
          const writableSwrKeyDocument = (swrKeyDocument ?? get().document.swrKeyDocument) as WritableDeep<
            typeof swrKeyDocument
          >;
          const writableSwrKeyCustomBlocks = (swrKeyCustomBlocks ?? get().document.swrKeyCustomBlocks) as WritableDeep<
            typeof swrKeyCustomBlocks
          >;
          const writableSwrKeyApiDefinition = (swrKeyApiDefinition ??
            get().document.swrKeyApiDefinition) as WritableDeep<typeof swrKeyApiDefinition>;

          state.document = {
            ...state.document,
            ...nextData,
            remoteDocumentChanged: false,
            swrKeyDocument: writableSwrKeyDocument ?? null,
            swrKeyCustomBlocks: writableSwrKeyCustomBlocks ?? null,
            swrKeyApiDefinition: writableSwrKeyApiDefinition ?? null,
          };

          // When running on the server, we must avoid marking this store as
          // "ready" to ensure it continues receiving updates until it gets
          // initialized on the client's first render.
          state.document.isReady = isClient;

          // When a page is "unrenderable", render the editor in raw mode.
          const isRenderable =
            isGuidesPage(state.document.data) || isReferencePage(state.document.data)
              ? (state.document.data.renderable?.status ?? true)
              : true;
          state.editor.isRawMode = !isRenderable;

          // Clear any existing MDX validation errors when initializing a new document.
          state.document.mdxValidationError = null;

          /**
           * For Reference pages, check if API Designer should have limited functionality:
           * 1. Check whether editing is disabled via `project.api_designer` at project level in top-level SuperHub store.
           * 2. Check whether any stats features are enabled for this endpoint. Make an exception for `stats.server_variables` b/c these are declared at a higher-level and shouldn't be impacted by edits.
           */
          if (isReferencePage(state.document.data)) {
            const allowedFeatures = ['server_variables', 'xml_responses', 'xml_requests', 'polymorphism', 'style'];

            const blockingFeatures = (
              Object.entries(state.document.data.api.stats) as [keyof typeof state.document.data.api.stats, boolean][]
            ).filter(([name, value]) => !allowedFeatures.includes(name) && value);

            state.document.apiDesignerLimited = {
              blockingFeatures: blockingFeatures.map(([name]) => name),
              byFeature: blockingFeatures.length > 0,
              byProjectLevel: projectStore.getState().data.api_designer.allow_editing !== 'enabled',
            };
          } else {
            state.document.apiDesignerLimited = null;
          }
        },
        false,
        actionLog('document.initialize', payload),
      );
    },

    setPrefetchFailed: prefetchFailed => {
      set(
        state => {
          state.document.prefetchFailed = prefetchFailed;
        },
        false,
        actionLog('document.setPrefetchFailed', prefetchFailed),
      );
    },

    setRemoteDocumentChanged: remoteDocumentChanged => {
      set(
        state => {
          state.document.remoteDocumentChanged = remoteDocumentChanged;
        },
        false,
        actionLog('document.setRemoteDocumentChanged', remoteDocumentChanged),
      );
    },

    revalidate: () => {
      return mutate(get().document.swrKeyDocument);
    },

    updateCustomBlock: block => {
      set(
        state => {
          const index = state.document.customBlocks.findIndex(({ tag }) => tag === block.tag);
          if (index === -1) {
            state.document.customBlocks.push(block);
          } else {
            state.document.customBlocks[index] = block;
          }
        },
        false,
        actionLog('document.updateCustomBlock', block),
      );

      // Update the page custom blocks SWR cache to the latest changes.
      mutate(get().document.swrKeyCustomBlocks, { data: get().document.customBlocks }, { revalidate: false });
    },

    updateDocument: async (nextDocument, options = {}) => {
      const { branch, skipEtag, slug, source } = options;
      const updateSource = source || 'user';
      const { data: document, getApiUrl } = get().document;
      if (!document) {
        throw new Error('Missing required page document');
      }

      const targetSlug = slug ?? document.slug;

      // Changelogs need a lang query param when i18n is enabled.
      const querystring =
        isChangelog(document) && get().isI18nEnabled ? new URLSearchParams({ lang: get().language }) : undefined;

      const requestUrl = getApiUrl({ slug: targetSlug, branch, querystring });
      if (!requestUrl) {
        throw new Error('Missing required request URL');
      }

      set(
        state => {
          state.document.isSaving = true;
          state.document.mdxValidationError = null;
          state.document.mdxValidationErrorPageContent = null;
          state.document.updateSource = updateSource;
        },
        false,
        actionLog('document.updateDocument.pending', nextDocument),
      );

      try {
        const currentEtag = !skipEtag ? get().document.etag : null;

        const response = await fetcher<{ data: SuperHubDocumentData }>(requestUrl, {
          body: JSON.stringify(nextDocument),
          method: 'PATCH',
          ...(currentEtag ? { headers: { 'If-Match': currentEtag } } : {}),
        });

        // 204 No Content — the API determined nothing changed. Keep current state.
        // Return null so callers know no server data was returned and can handle
        // form resets appropriately (e.g. reset to submitted values, not stale store data).
        if (!response || typeof response === 'string') {
          set(
            state => {
              state.document.isSaving = false;
              state.document.remoteDocumentChanged = false;
            },
            false,
            actionLog('document.updateDocument.unchanged', nextDocument),
          );
          return null;
        }

        const { data } = response;
        const newEtag = response.__headers__?.etag ?? null;

        let customBlocks: ReadCustomBlockGitCollectionType['data'] = [];
        const isCustomBlockPage = isGuidesPage(document) || isReferencePage(document);
        if (isCustomBlockPage) {
          /**
           * Re-computed request URL for custom blocks in case the document slug
           * gets renamed in the patch request above.
           */
          const customBlocksUrl = getApiUrl({ slug: data.slug, branch });
          const { data: customBlocksData } = await fetcher<ReadCustomBlockGitCollectionType>(
            `${customBlocksUrl}/custom_blocks`,
            {},
          );
          customBlocks = customBlocksData;
        }

        set(
          state => {
            state.document.data = data;
            state.document.etag = newEtag;
            state.document.isSaving = false;
            state.document.mdxValidationError = null;
            state.document.mdxValidationErrorPageContent = null;
            state.document.remoteDocumentChanged = false;

            state.document.customBlocks = customBlocks;
          },
          false,
          actionLog('document.updateDocument.fulfilled', nextDocument),
        );

        // Update page document SWR cache to the latest changes.
        mutate<{ data: typeof data }>(get().document.swrKeyDocument, { data }, { revalidate: false });

        mutate<{ data: typeof customBlocks }>(
          get().document.swrKeyCustomBlocks,
          { data: customBlocks },
          { revalidate: false },
        );

        // Mutate the SWR cache for the document's API definition URI
        if (isReferencePage(data) && get().document.swrKeyApiDefinition) {
          mutate<{ data: typeof data.api }>(
            get().document.swrKeyApiDefinition,
            { data: data.api },
            { revalidate: false },
          );
        }

        // Revalidate the sidebar to include this newly created page.
        get().sidebar.revalidate();
        return data;
      } catch (error) {
        const { info } = error as HTTPError & { info: ErrorType };
        const mdxValidationError = info?.title?.match(/MDX validation/) ? info.detail : null;

        set(
          state => {
            state.document.isSaving = false;
            state.document.saveError = info;
            if (mdxValidationError) {
              state.document.mdxValidationError = mdxValidationError;
              state.document.mdxValidationErrorPageContent = nextDocument.content?.body ?? null;
              state.editor.isSavingDisabled = true;
            }
          },
          false,
          actionLog('document.updateDocument.rejected', nextDocument),
        );
        throw error;
      }
    },

    setUpdateSource: updateSource => {
      set(
        state => {
          state.document.updateSource = updateSource;
        },
        false,
        actionLog('document.setUpdateSource', { updateSource }),
      );
    },
  },
});

export * from './ConnectSuperHubDocumentToApi';
export * from './InitializeSuperHubDocument';
